import React, { Suspense, useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import {
  getWidgetComponent,
  WidgetConfigPropTypes
} from 'Screens/Contextualization/Dashboard/registry';
import {
  Grid,
  Row,
  GridItem,
  EditToolbar,
  DragHandleIcon,
  SettingsIconButton
} from './styled';
import filterAndOrderWidgets from './utils/filterAndOrderWidgets';
import reorderLayout from './utils/reorderLayout';
import { getAddableWidgets } from './constants/widgetLabels';
import SpinnerComponent from 'Common/Components/SpinnerContainer';
import WidgetDrawer from './components/WidgetDrawer';
import WidgetPicker from './components/WidgetPicker';

/**
 * Dynamic dashboard grid that renders widgets based on a row-based configuration.
 * Supports an edit mode where widgets can be reordered across rows via drag-and-drop.
 *
 * @param {Object} props
 * @param {Array<Array>} props.rows - Row-based layout (array of arrays of widget configs)
 * @param {boolean} [props.isEditMode] - Enables drag-and-drop editing
 * @param {Function} [props.onLayoutChange] - Called with new rows when layout changes
 */
const DashboardGrid = ({ rows, isEditMode, onLayoutChange, showPicker, onClosePicker }) => {
  const [selectedWidget, setSelectedWidget] = useState(null);

  const visibleRows = filterAndOrderWidgets(rows);

  const availableWidgets = useMemo(() => {
    const currentIds = new Set(visibleRows.flat().map(w => w.id));
    return getAddableWidgets().filter(id => !currentIds.has(id));
  }, [visibleRows]);

  const handleDragEnd = useCallback(
    ({ source, destination }) => {
      if (!destination) return;
      if (
        source.droppableId === destination.droppableId &&
        source.index === destination.index
      ) { return; }
      const newRows = reorderLayout(visibleRows, source, destination);
      onLayoutChange(newRows);
    },
    [visibleRows, onLayoutChange]
  );

  const handleWidgetUpdate = useCallback(
    (rowIndex, colIndex, changes) => {
      const newRows = visibleRows.map((row, ri) =>
        row.map((widget, ci) =>
          ri === rowIndex && ci === colIndex ? { ...widget, ...changes } : widget
        )
      );
      onLayoutChange(newRows);
      setSelectedWidget(prev => (prev ? { ...prev, ...changes } : null));
    },
    [visibleRows, onLayoutChange]
  );

  const handleWidgetRemove = useCallback(
    (rowIndex, colIndex) => {
      const newRows = visibleRows
        .map((row, ri) =>
          ri === rowIndex ? row.filter((_, ci) => ci !== colIndex) : row
        )
        .filter(row => row.length > 0);
      onLayoutChange(newRows);
      setSelectedWidget(null);
    },
    [visibleRows, onLayoutChange]
  );

  const handleAddWidget = useCallback(
    widgetId => {
      const newRows = [...visibleRows, [{ id: widgetId, visible: true }]];
      onLayoutChange(newRows);
      onClosePicker();
    },
    [visibleRows, onLayoutChange, onClosePicker]
  );

  return (
    <>
      <DragDropContext onDragEnd={ handleDragEnd }>
        <Grid>
          {visibleRows.map((row, rowIndex) => {
            const rowId = row.map(w => w.id).join('|');
            return (
            <Droppable
              key={ rowId }
              droppableId={ rowId }
              direction="horizontal"
              isDropDisabled={ !isEditMode }
            >
              {(provided, snapshot) => (
                <Row
                  ref={ provided.innerRef }
                  { ...provided.droppableProps }
                  $isDraggingOver={ isEditMode && snapshot.isDraggingOver }
                >
                  {row.map(({ id, visible, grow, ...widgetProps }, colIndex) => {
                    const WidgetComponent = getWidgetComponent(id);

                    if (!WidgetComponent) {
                      // eslint-disable-next-line no-console
                      console.warn(
                        `[DashboardGrid] Widget "${id}" not defined in widgetRegistry. Skipping render.`
                      );
                      return null;
                    }

                    const isSelected =
                      selectedWidget?.rowIndex === rowIndex &&
                      selectedWidget?.colIndex === colIndex;

                    return (
                      <Draggable key={ id } draggableId={ id } index={ colIndex } isDragDisabled={ !isEditMode }>
                        {(dragProvided, dragSnapshot) => (
                          <GridItem
                            ref={ dragProvided.innerRef }
                            { ...dragProvided.draggableProps }
                            grow={ grow }
                            $isEditMode={ isEditMode }
                            $isDragging={ isEditMode && dragSnapshot.isDragging }
                            $isSelected={ isEditMode && isSelected }
                          >
                            {isEditMode && (
                              <EditToolbar>
                                <DragHandleIcon { ...dragProvided.dragHandleProps } />
                                <SettingsIconButton
                                  onClick={ () =>
                                    setSelectedWidget({ id, grow, ...widgetProps, rowIndex, colIndex })
                                  }
                                  title="Widget settings"
                                >
                                  ⚙
                                </SettingsIconButton>
                              </EditToolbar>
                            )}
                            <Suspense fallback={ <SpinnerComponent height="250px" /> }>
                              <WidgetComponent { ...widgetProps } />
                            </Suspense>
                          </GridItem>
                        )}
                      </Draggable>
                    );
                  })}
                  {provided.placeholder}
                </Row>
              )}
            </Droppable>
            );
          })}
        </Grid>
      </DragDropContext>

      {selectedWidget && (
        <WidgetDrawer
          widget={ selectedWidget }
          onClose={ () => setSelectedWidget(null) }
          onUpdate={ changes =>
            handleWidgetUpdate(
              selectedWidget.rowIndex,
              selectedWidget.colIndex,
              changes
            )
          }
          onRemove={ () =>
            handleWidgetRemove(selectedWidget.rowIndex, selectedWidget.colIndex)
          }
        />
      )}
      {showPicker && (
        <WidgetPicker
          availableWidgets={ availableWidgets }
          onAdd={ handleAddWidget }
          onClose={ onClosePicker }
        />
      )}
    </>
  );
};

DashboardGrid.defaultProps = {
  isEditMode: false,
  onLayoutChange: () => {},
  showPicker: false,
  onClosePicker: () => {}
};

DashboardGrid.propTypes = {
  rows: PropTypes.arrayOf(
    PropTypes.arrayOf(PropTypes.shape(WidgetConfigPropTypes))
  ).isRequired,
  isEditMode: PropTypes.bool,
  onLayoutChange: PropTypes.func,
  showPicker: PropTypes.bool,
  onClosePicker: PropTypes.func
};

export default DashboardGrid;
